import { makeAutoObservable } from "mobx";
import RootStore from "stores/RootStore";

// Types
export interface BaySection {
  id: string;
  name: string;
  type: 'loading' | 'staging' | 'storage';
  capacity: number;
  currentUsage: number;
  unit: string;
  status: 'active' | 'idle' | 'maintenance';
}

export interface QAInspection {
  id: string;
  date: string;
  inspector: string;
  result: 'pass' | 'fail' | 'pending';
  defectType?: string;
  notes?: string;
}

export interface BayQAStats {
  inspectedToday: number;
  passedToday: number;
  failedToday: number;
  pendingInspection: number;
  passRate: number;
  recentInspections: QAInspection[];
}

export interface Bay {
  id: string;
  name: string;
  sections: BaySection[];
  unitsToday: number;
  yardsToday: number;
  status: 'active' | 'idle' | 'maintenance';
  qaStats: BayQAStats;
}

export interface PlantArea {
  id: string;
  name: string;
  type: 'batch_plant' | 'office';
  status: 'active' | 'idle' | 'maintenance';
  dailyCapacity?: number;
  todayProduction?: number;
}

// Mock data based on floor plan - 3 Bays + Batch Plants
const MOCK_BAYS: Bay[] = [
  {
    id: 'bay-1',
    name: 'Bay 1',
    status: 'active',
    unitsToday: 24,
    yardsToday: 245,
    sections: [
      { id: 'bay1-loading', name: 'Loading Zone', type: 'loading', capacity: 100, currentUsage: 65, unit: '%', status: 'active' },
      { id: 'bay1-staging', name: 'Staging Area', type: 'staging', capacity: 100, currentUsage: 72, unit: '%', status: 'active' },
      { id: 'bay1-storage', name: 'Storage', type: 'storage', capacity: 100, currentUsage: 45, unit: '%', status: 'idle' },
    ],
    qaStats: {
      inspectedToday: 22,
      passedToday: 20,
      failedToday: 2,
      pendingInspection: 2,
      passRate: 91,
      recentInspections: [
        { id: 'insp-1-1', date: '2024-12-15 14:30', inspector: 'Mike Chen', result: 'pass', notes: 'Surface finish excellent' },
        { id: 'insp-1-2', date: '2024-12-15 13:45', inspector: 'Sarah Johnson', result: 'fail', defectType: 'Surface cracks', notes: 'Minor hairline cracks detected' },
        { id: 'insp-1-3', date: '2024-12-15 12:20', inspector: 'Mike Chen', result: 'pass' },
        { id: 'insp-1-4', date: '2024-12-15 11:15', inspector: 'Tom Wilson', result: 'pass' },
        { id: 'insp-1-5', date: '2024-12-15 10:00', inspector: 'Sarah Johnson', result: 'fail', defectType: 'Dimensional variance', notes: 'Out of tolerance by 3mm' },
      ],
    },
  },
  {
    id: 'bay-2',
    name: 'Bay 2',
    status: 'active',
    unitsToday: 36,
    yardsToday: 380,
    sections: [
      { id: 'bay2-loading', name: 'Loading Zone', type: 'loading', capacity: 100, currentUsage: 85, unit: '%', status: 'active' },
      { id: 'bay2-staging', name: 'Staging Area', type: 'staging', capacity: 100, currentUsage: 68, unit: '%', status: 'active' },
      { id: 'bay2-storage', name: 'Storage', type: 'storage', capacity: 100, currentUsage: 52, unit: '%', status: 'active' },
    ],
    qaStats: {
      inspectedToday: 34,
      passedToday: 32,
      failedToday: 2,
      pendingInspection: 2,
      passRate: 94,
      recentInspections: [
        { id: 'insp-2-1', date: '2024-12-15 14:45', inspector: 'Tom Wilson', result: 'pass' },
        { id: 'insp-2-2', date: '2024-12-15 14:00', inspector: 'Mike Chen', result: 'pass' },
        { id: 'insp-2-3', date: '2024-12-15 13:15', inspector: 'Tom Wilson', result: 'pass' },
        { id: 'insp-2-4', date: '2024-12-15 12:30', inspector: 'Sarah Johnson', result: 'fail', defectType: 'Air voids', notes: 'Excessive air pockets on surface' },
        { id: 'insp-2-5', date: '2024-12-15 11:45', inspector: 'Mike Chen', result: 'pass' },
      ],
    },
  },
  {
    id: 'bay-3',
    name: 'Bay 3',
    status: 'active',
    unitsToday: 18,
    yardsToday: 195,
    sections: [
      { id: 'bay3-loading', name: 'Loading Zone', type: 'loading', capacity: 100, currentUsage: 55, unit: '%', status: 'active' },
      { id: 'bay3-staging', name: 'Staging Area', type: 'staging', capacity: 100, currentUsage: 40, unit: '%', status: 'idle' },
      { id: 'bay3-storage', name: 'Storage', type: 'storage', capacity: 100, currentUsage: 30, unit: '%', status: 'maintenance' },
    ],
    qaStats: {
      inspectedToday: 16,
      passedToday: 15,
      failedToday: 1,
      pendingInspection: 2,
      passRate: 94,
      recentInspections: [
        { id: 'insp-3-1', date: '2024-12-15 14:15', inspector: 'Sarah Johnson', result: 'pass' },
        { id: 'insp-3-2', date: '2024-12-15 13:30', inspector: 'Tom Wilson', result: 'pass' },
        { id: 'insp-3-3', date: '2024-12-15 12:45', inspector: 'Mike Chen', result: 'fail', defectType: 'Reinforcement exposure', notes: 'Rebar visible at edge' },
        { id: 'insp-3-4', date: '2024-12-15 11:30', inspector: 'Sarah Johnson', result: 'pass' },
        { id: 'insp-3-5', date: '2024-12-15 10:30', inspector: 'Tom Wilson', result: 'pass' },
      ],
    },
  },
];

const MOCK_PLANT_AREAS: PlantArea[] = [
  { id: 'new-batch-plant', name: 'New Batch Plant', type: 'batch_plant', status: 'active', dailyCapacity: 1500, todayProduction: 620 },
  { id: 'old-batch-plant', name: 'Old Batch Plant', type: 'batch_plant', status: 'idle', dailyCapacity: 800, todayProduction: 200 },
  { id: 'qa-office', name: 'QA Office', type: 'office', status: 'active' },
];

class PlantCapacityStore {
  rootStore: RootStore;

  // Data
  bays: Bay[] = [...MOCK_BAYS];
  plantAreas: PlantArea[] = [...MOCK_PLANT_AREAS];

  // UI State
  selectedBayId: string | null = null;
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Computed: Selected bay
  get selectedBay(): Bay | null {
    if (!this.selectedBayId) return null;
    return this.bays.find(b => b.id === this.selectedBayId) || null;
  }

  // Computed: Total stats
  get totalYardsToday(): number {
    return this.bays.reduce((sum, b) => sum + b.yardsToday, 0);
  }

  get totalUnitsToday(): number {
    return this.bays.reduce((sum, b) => sum + b.unitsToday, 0);
  }

  // Computed: QA stats
  get totalInspectedToday(): number {
    return this.bays.reduce((sum, b) => sum + b.qaStats.inspectedToday, 0);
  }

  get totalPassedToday(): number {
    return this.bays.reduce((sum, b) => sum + b.qaStats.passedToday, 0);
  }

  get totalFailedToday(): number {
    return this.bays.reduce((sum, b) => sum + b.qaStats.failedToday, 0);
  }

  get overallPassRate(): number {
    const total = this.totalInspectedToday;
    return total > 0 ? Math.round((this.totalPassedToday / total) * 100) : 0;
  }

  // Get QA pass rate color
  getQAPassRateColor = (rate: number): string => {
    if (rate >= 90) return '#4caf50';
    if (rate >= 80) return '#ff9800';
    return '#f44336';
  };

  // Computed: Batch plant stats
  get newBatchPlant(): PlantArea | undefined {
    return this.plantAreas.find(p => p.id === 'new-batch-plant');
  }

  get oldBatchPlant(): PlantArea | undefined {
    return this.plantAreas.find(p => p.id === 'old-batch-plant');
  }

  get totalDailyCapacity(): number {
    return this.plantAreas
      .filter(p => p.type === 'batch_plant')
      .reduce((sum, p) => sum + (p.dailyCapacity || 0), 0);
  }

  get totalDailyProduction(): number {
    return this.plantAreas
      .filter(p => p.type === 'batch_plant')
      .reduce((sum, p) => sum + (p.todayProduction || 0), 0);
  }

  get productionProgress(): number {
    return this.totalDailyCapacity > 0 ? (this.totalDailyProduction / this.totalDailyCapacity) * 100 : 0;
  }

  // Get bay average utilization
  getBayUtilization = (bay: Bay): number => {
    if (bay.sections.length === 0) return 0;
    const total = bay.sections.reduce((sum, s) => sum + s.currentUsage, 0);
    return total / bay.sections.length;
  };

  // Get utilization color
  getUtilizationColor = (percent: number): string => {
    if (percent >= 75) return '#4caf50';
    if (percent >= 50) return '#ff9800';
    if (percent >= 25) return '#2196f3';
    return '#9e9e9e';
  };

  // Get status color
  getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return '#4caf50';
      case 'idle': return '#ff9800';
      case 'maintenance': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  // Actions
  selectBay = (bayId: string | null) => {
    this.selectedBayId = bayId;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.selectedBayId = null;
    this.loading = false;
    this.error = null;
  };
}

export default PlantCapacityStore;
